<template>
<!-- Tabs are in-page navigation, not an outside click — clicks inside the
     strip must not bubble into the layout's drawer-closing click handler. -->
    <div v-if="tabs.length > 1 || showSingleTab" class="tabs-wrapper" @click.stop>
        <div ref="scrollContainer" class="tabs" @scroll="updateScrollState">
            <template v-for="tab in tabs" :key="tab.name">
                <a
                    v-if="tab.href && tab.external"
                    :href="tab.href"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="tabs__tab tabs__tab--standard"
                    draggable="false"
                >
                    {{ tab.name }}
                    <icon icon="phosphor/regular/arrow-up-right" class="size-3"></icon>
                </a>
                <inertia-link
                    v-else-if="tab.href"
                    :href="tab.href"
                    class="tabs__tab"
                    :class="[isCurrent(tab.current) ? 'tabs__tab--current' : 'tabs__tab--standard']"
                    draggable="false"
                >
                    {{ tab.name }}
                </inertia-link>
                <button
                    v-else
                    type="button"
                    class="tabs__tab"
                    :class="[isCurrent(tab.current) ? 'tabs__tab--current' : 'tabs__tab--standard']"
                    @click="tab.click"
                >
                    {{ tab.name }}
                </button>
            </template>
        </div>
        <button
            type="button"
            class="tabs-scroll tabs-scroll--left"
            :aria-disabled="!canScrollLeft"
            :tabindex="canScrollLeft ? 0 : -1"
            aria-label="Scroll left"
            @click="scrollLeft"
        >
            <icon icon="phosphor/regular/caret-left" class="size-4"></icon>
        </button>
        <button
            type="button"
            class="tabs-scroll tabs-scroll--right"
            :aria-disabled="!canScrollRight"
            :tabindex="canScrollRight ? 0 : -1"
            aria-label="Scroll right"
            @click="scrollRight"
        >
            <icon icon="phosphor/regular/caret-right" class="size-4"></icon>
        </button>
    </div>
</template>

<script setup lang="ts">
import { toRef } from 'vue';
import { useDragScroll } from '@src/Common/Composables/useDragScroll';
import { useScrollContainer } from '@src/Common/Composables/useScrollContainer';

type Tab = {
    name: string;
    href?: string;
    external?: boolean;
    current: boolean | (() => boolean);
    click?: () => void;
};

interface Props {
    tabs?: Tab[];
    showSingleTab?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    tabs: (): Tab[] => [],
    showSingleTab: false,
});

const tabs = toRef(props, 'tabs');

const isCurrent = (current: boolean | (() => boolean)) => {
    return typeof current === 'function' ? current() : current;
};

// Drag-to-scroll on the strip (same pattern as NavigationSubmenu) + left/right
// overlay arrows. The arrows sit absolute-positioned over the strip, so they
// don't reserve horizontal space when hidden and don't shift the layout.
const { scrollContainer } = useDragScroll({ dragThreshold: 5 });
const { canScrollLeft, canScrollRight, scrollLeft, scrollRight, updateScrollState } = useScrollContainer({
    scrollContainer,
});
</script>
